import { useQuery } from "@tanstack/react-query";
import { CalendarClock, ShieldCheck } from "lucide-react";
import { Navigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { isNewScheduledOrderCycleEnabled } from "@/features/newScheduledOrderCycleFeature";
import { scheduledOrderCutoffPoliciesApi } from "@/lib/api";
import { Layout } from "@/components/Layout";

const orderTypeLabels: Record<string, string> = {
  "02": "2º semestre",
};

export default function ScheduledOrderCutoffPolicies() {
  const { data, isLoading, isError } = useQuery( {
    queryKey: [ "scheduled-order-cutoff-policies" ],
    queryFn: scheduledOrderCutoffPoliciesApi.getAll,
    enabled: isNewScheduledOrderCycleEnabled,
  } );

  if ( !isNewScheduledOrderCycleEnabled ) {
    return <Navigate to="/admin-pedidos" replace />;
  }

  const policies = data?.data ?? [];

  return (
    <Layout title="Políticas de Corte">
      <div className="space-y-6">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <CalendarClock className="h-5 w-5" />
          <p>Datas oficiais usadas pelo backend para controlar o fechamento dos pedidos programados.</p>
        </div>

        <Card className="shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-700" />
              Configuração vigente
            </CardTitle>
            <Badge variant="outline">{policies.length} política{policies.length === 1 ? "" : "s"}</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-6">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : isError ? (
            <div className="p-8 text-center text-sm text-destructive">
              Não foi possível carregar as políticas de corte.
            </div>
          ) : policies.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Nenhuma política de corte cadastrada para ciclos novos.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ciclo</TableHead>
                  <TableHead>Tipo de pedido</TableHead>
                  <TableHead>Data de corte</TableHead>
                  <TableHead>Fuso horário</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Última alteração</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {policies.map( policy => (
                  <TableRow key={policy.id}>
                    <TableCell className="font-semibold">{policy.cycleYear}</TableCell>
                    <TableCell>{orderTypeLabels[ policy.orderType ] ?? policy.orderType}</TableCell>
                    <TableCell>
                      <div className="font-medium">{new Date( policy.cutoffAt ).toLocaleString( "pt-BR" )}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{policy.timeZone}</TableCell>
                    <TableCell>
                      <Badge variant={policy.isActive ? "default" : "secondary"}>
                        {policy.isActive ? "Ativa" : "Inativa"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {new Date( policy.updatedAt || policy.createdAt ).toLocaleString( "pt-BR" )}
                      </div>
                      {( policy.updatedBy || policy.createdBy ) && (
                        <div className="text-xs text-muted-foreground">por {policy.updatedBy || policy.createdBy}</div>
                      )}
                    </TableCell>
                  </TableRow>
                ) )}
              </TableBody>
            </Table>
          )}
        </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          Nesta etapa a consulta é somente leitura. A alteração da política continua sendo feita diretamente no banco de DEV.
        </p>
      </div>
    </Layout>
  );
}
